// src/components/CoachView.js
import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { BACKEND_URL } from "../config";

function CoachView({ token, userName, onOpenGoalModal, currentView, mode = "chat", showToast }) {
  const initialMessage = useMemo(
    () => ({
      role: "assistant",
      content: `Hey ${userName || "there"}! 👋 I'm your AI fitness coach. I have access to your food logs, exercise history, goals, and wearable data to give you personalized advice.\n\nAsk me anything about:\n• Your nutrition and eating habits\n• Workout recommendations\n• Progress towards your goals\n• Tips to stay on track\n• Meal suggestions based on your targets\n\nWhat would you like to know?`,
    }),
    [userName]
  );

  const [messages, setMessages] = useState([initialMessage]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [dateRange, setDateRange] = useState(30);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [pendingCreatedGoalId, setPendingCreatedGoalId] = useState("");


  const messagesEndRef = useRef(null);
  const asList = (value) => (Array.isArray(value) ? value : []);

  const PROGRAM_PROFILES = [
    { value: "fat_loss_beginner", label: "Fat Loss (Beginner)" },
    { value: "fat_loss_intermediate", label: "Fat Loss (Intermediate)" },
    { value: "strength_3day", label: "Strength (3 days/week)" },
    { value: "strength_4day", label: "Strength (4 days/week)" },
    { value: "hypertrophy_4day", label: "Hypertrophy (4 days/week)" },
    { value: "endurance_beginner", label: "Endurance (Beginner)" },
    { value: "recomp_general", label: "Recomp (General)" },
  ];

  // ----------------------------
  // Programs
  // ----------------------------
  const [programs, setPrograms] = useState([]);
  const [programsLoading, setProgramsLoading] = useState(false);
  const programPollRef = useRef(null);

  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedProgramMeta, setSelectedProgramMeta] = useState(null);
  const [programLoading, setProgramLoading] = useState(false);

  const loadPrograms = useCallback(async () => {
    if (!token) return;
    setProgramsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/coach/programs`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setPrograms(data.programs || []);
    } catch (e) {
      console.warn("Failed to load programs:", e?.message || e);
      setPrograms([]);
    } finally {
      setProgramsLoading(false);
    }
  }, [token]);

  const loadProgram = useCallback(
    async (programId) => {
      if (!token || !programId) return;
      setProgramLoading(true);
      try {
        const res = await fetch(`${BACKEND_URL}/api/coach/programs/${encodeURIComponent(programId)}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        setSelectedProgram(data.program || null);
        setSelectedProgramMeta(data.detail || data.created || null);
      } catch (e) {
        console.warn("Failed to load program:", e?.message || e);
        setSelectedProgram(null);
        setSelectedProgramMeta(null);
      } finally {
        setProgramLoading(false);
      }
    },
    [token]
  );

  const deleteProgram = useCallback(
    async (programId) => {
      if (!token || !programId) return;
      const ok = window.confirm("Delete this program? This will also remove its generated habits/events/goals (created by the program).");
      if (!ok) return;

      try {
        const res = await fetch(`${BACKEND_URL}/api/coach/programs/${encodeURIComponent(programId)}`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });

        if (!res.ok) {
          const txt = await res.text();
          throw new Error(txt || "Delete failed");
        }

        // If deleting currently open program, clear it
        setSelectedProgram((prev) => (prev?.program_id === programId ? null : prev));
        setSelectedProgramMeta((prev) => (selectedProgram?.program_id === programId ? null : prev));

        await loadPrograms();
      } catch (e) {
        showToast?.("Error deleting program: " + (e?.message || String(e)), "error");
      }
    },
    [token, loadPrograms, selectedProgram, showToast]
  );

  useEffect(() => {
    loadPrograms();
  }, [loadPrograms]);

  useEffect(() => {
    const hasRunning = Array.isArray(programs) && programs.some((p) => String(p.status || "").toLowerCase() === "running");
    if (!hasRunning) {
      if (programPollRef.current) clearInterval(programPollRef.current);
      programPollRef.current = null;
      return;
    }
    if (programPollRef.current) return;
    programPollRef.current = setInterval(() => { loadPrograms(); }, 5000);
    return () => { if (programPollRef.current) { clearInterval(programPollRef.current); programPollRef.current = null; } };
  }, [programs, loadPrograms]);

  // ----------------------------
  // Create Program modal
  // ----------------------------
  const [showCreateProgram, setShowCreateProgram] = useState(false);
  const [profileKey, setProfileKey] = useState(PROGRAM_PROFILES[0]?.value || "recomp_general");

  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goals, setGoals] = useState([]); // active goals
  const [selectedGoalIds, setSelectedGoalIds] = useState({}); // { [goal_id]: boolean }
  const [createProgramLoading, setCreateProgramLoading] = useState(false);

  const loadGoals = useCallback(async () => {
    if (!token) return;
    setGoalsLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/goals/list?user_id=me`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();

      const list = (data?.goals || []).filter((g) => !g?.completed);
      setGoals(list);

      setSelectedGoalIds((prev) => {
        const next = { ...prev };
        for (const g of list) {
          if (next[g.goal_id] === undefined) next[g.goal_id] = false;
        }
        for (const k of Object.keys(next)) {
          if (!list.some((g) => g.goal_id === k)) delete next[k];
        }
        return next;
      });
    } catch (e) {
      console.warn("Failed to load goals:", e?.message || e);
      setGoals([]);
      setSelectedGoalIds({});
    } finally {
      setGoalsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!showCreateProgram) return;
    loadGoals();
  }, [showCreateProgram, loadGoals]);

  useEffect(() => {
    if (!showCreateProgram || !pendingCreatedGoalId || goalsLoading) return;
    setSelectedGoalIds((prev) => ({
      ...prev,
      [pendingCreatedGoalId]: true,
    }));
  }, [showCreateProgram, pendingCreatedGoalId, goalsLoading]);

  useEffect(() => {
  const onGoalCreated = () => {
    if (showCreateProgram) {
      loadGoals();
    }
  };

  const onCoachProgramGoalCreated = async (event) => {
    const createdGoalId = event?.detail?.goal_id || event?.detail?.id || "";
    setPendingCreatedGoalId(createdGoalId);
    setShowCreateProgram(true);
    await loadGoals();
    if (createdGoalId) {
      setSelectedGoalIds((prev) => ({
        ...prev,
        [createdGoalId]: true,
      }));
    }
  };

  window.addEventListener("goal:created", onGoalCreated);
  window.addEventListener("goals:refresh", onGoalCreated);
  window.addEventListener("coach_programs:return_goal_created", onCoachProgramGoalCreated);
  return () => {
    window.removeEventListener("goal:created", onGoalCreated);
    window.removeEventListener("goals:refresh", onGoalCreated);
    window.removeEventListener("coach_programs:return_goal_created", onCoachProgramGoalCreated);
  };
}, [showCreateProgram, loadGoals]);

  const toggleGoal = (goalId) => {
    setSelectedGoalIds((prev) => ({ ...prev, [goalId]: !prev[goalId] }));
  };

  const createProgram = async () => {
    if (!token) return;

    const pickedGoalIds = Object.entries(selectedGoalIds)
      .filter(([, v]) => !!v)
      .map(([k]) => k);

    if (!profileKey) {
      showToast?.("Please select a profile type.", "error");
      return;
    }
    if (pickedGoalIds.length === 0) {
      showToast?.("Pick at least one goal for the program (or create a new goal first).", "error");
      return;
    }

    setCreateProgramLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/coach/programs`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          profile_key: profileKey,
          weeks: 4,
          goals: pickedGoalIds.map((id) => ({ goal_id: id })),
          background: true,
        }),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(txt || "Failed to create program");
      }

      const out = await res.json();
      await loadPrograms();

      if (out?.job_id) {
        showToast?.("✅ Program generation started in background.");
      }

      setShowCreateProgram(false);
      setProfileKey(PROGRAM_PROFILES[0]?.value || "recomp_general");
      setSelectedGoalIds({});
    } catch (e) {
      showToast?.("Error creating program: " + (e?.message || String(e)), "error");
    } finally {
      setCreateProgramLoading(false);
    }
  };

  // ----------------------------
  // Close modals on nav change
  // ----------------------------
  useEffect(() => {
    setShowCreateProgram(false);
    setSelectedProgram(null);
    setSelectedProgramMeta(null);
  }, [currentView]);
  useEffect(() => {
  const onOpen = () => setShowCreateProgram(true);
  window.addEventListener("coach:openCreateProgram", onOpen);
  return () => window.removeEventListener("coach:openCreateProgram", onOpen);
}, []);
  // ----------------------------
  // Chat history + messaging
  // ----------------------------
  useEffect(() => {
    setMessages((prev) => {
      if (!prev?.length) return [initialMessage];
      if (prev.length === 1 && prev[0]?.role === "assistant") return [initialMessage];
      return prev;
    });
  }, [initialMessage]);

  const shouldAutoScrollRef = useRef(false);
  const prevLenRef = useRef(0);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    const prevLen = prevLenRef.current;
    const nextLen = messages?.length || 0;

    // only scroll if we explicitly asked for it (sendMessage / loading bubble)
    if (shouldAutoScrollRef.current && nextLen > prevLen) {
      scrollToBottom();
    }

    prevLenRef.current = nextLen;
    shouldAutoScrollRef.current = false;
  }, [messages, scrollToBottom]);


  const loadChatHistory = useCallback(async () => {
    if (!token) {
      setHistoryLoaded(true);
      return;
    }

    try {
      const response = await fetch(`${BACKEND_URL}/api/coach/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.messages && Array.isArray(data.messages) && data.messages.length > 0) {
          setMessages(data.messages);
        }
      }
    } catch (error) {
      console.error("Error loading chat history:", error);
    } finally {
      setHistoryLoaded(true);
    }
  }, [token]);

  const saveChatHistory = useCallback(
    async (msgs) => {
      if (!token) return;

      try {
        await fetch(`${BACKEND_URL}/api/coach/save`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ messages: msgs }),
        });
      } catch (error) {
        console.error("Error saving chat history:", error);
      }
    },
    [token]
  );

  useEffect(() => {
    loadChatHistory();
  }, [loadChatHistory]);

  useEffect(() => {
    if (!historyLoaded) return;
    if (!messages || messages.length <= 1) return;
    saveChatHistory(messages);
  }, [messages, historyLoaded, saveChatHistory]);

  const clearChatHistory = async () => {
    if (!window.confirm("Are you sure you want to clear your chat history?")) return;

    try {
      if (token) {
        await fetch(`${BACKEND_URL}/api/coach/history`, {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        });
      }
      setMessages([initialMessage]);
    } catch (error) {
      console.error("Error clearing chat history:", error);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || loading) return;

    shouldAutoScrollRef.current = true;
    const userMessage = input.trim();
    setInput("");

    setMessages((prev) => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/coach/ask`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ question: userMessage, days: dateRange }),
      });

      const data = await response.json();

      if (data?.success) {
        shouldAutoScrollRef.current = true;
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: data.response,
            context: data.context_summary,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            role: "assistant",
            content: "Sorry, I had trouble processing that. Please try again.",
            error: true,
          },
        ]);
      }
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: "Sorry, I couldn't connect to the AI service. Please try again.",
          error: true,
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const quickQuestions = [
    "How am I doing with my calorie goals?",
    "What should I eat for dinner?",
    "Am I exercising enough?",
    "How can I lose weight faster?",
    "Give me a workout suggestion",
    "Analyze my eating patterns",
  ];

  const dateRangeOptions = [
    { label: "7 days", value: 7 },
    { label: "30 days", value: 30 },
    { label: "90 days", value: 90 },
    { label: "6 months", value: 180 },
    { label: "All time", value: 3650 },
  ];

  return (
    <div className="container coach-page" style={{ maxWidth: "980px" }}>
      {/* Header */}
      <div className="card coach-header-card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <div
            style={{
              width: 56,
              height: 56,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              borderRadius: 16,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 28,
            }}
          >
            🤖
          </div>
          <div>
            <h1 className="heading-2" style={{ margin: 0 }}>{mode === "programs" ? "Coach Programs" : "AI Fitness Coach"}</h1>
            <p className="body-small" style={{ color: "var(--text-secondary)", margin: 0 }}>
              {mode === "programs" ? "Generate and manage your programs" : "Personalized advice based on your data"}
            </p>
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="body-small" style={{ color: "var(--text-secondary)" }}>Data range:</span>
            <select
              value={dateRange}
              onChange={(e) => setDateRange(parseInt(e.target.value))}
              className="form-input"
              style={{ width: "auto", padding: "8px 12px", margin: 0 }}
              data-testid="coach-date-range"
            >
              {dateRangeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {messages.length > 1 && (
            <button onClick={clearChatHistory} className="btn-secondary" style={{ padding: "8px 12px", fontSize: 14 }} data-testid="clear-chat-btn">
              Clear History
            </button>
          )}
        </div>
      </div>

            </div>

{mode === "programs" && (
        <>
      {/* Programs Panel */}
      <div className="card" style={{ padding: 16, marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <div className="heading-4" style={{ marginBottom: 4 }}>Programs</div>
            <div className="body-small" style={{ color: "var(--text-secondary)" }}>
              View your generated programs and what they created.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            <button type="button" className="btn-primary" onClick={() => setShowCreateProgram(true)} data-testid="programs-create">
              + Create Program
            </button>

            <button type="button" className="btn-secondary" onClick={loadPrograms} disabled={programsLoading} style={{ padding: "8px 12px" }} data-testid="programs-refresh">
              {programsLoading ? "…" : "Refresh"}
            </button>
          </div>
        </div>

        <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr", gap: 10 }}>
          {programsLoading ? (
            <div className="body-small" style={{ color: "var(--text-muted)" }}>Loading programs…</div>
          ) : programs?.length ? (
            programs.map((p) => (
              <div
                key={p.program_id}
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 12,
                  padding: 12,
                  borderRadius: 12,
                  border: "1px solid var(--border-light)",
                  background: "var(--bg-secondary)",
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 800, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {p.name || "Program"}
                  </div>
                  <div className="body-small" style={{ color: "var(--text-muted)" }}>
                    {p.profile_key ? `Profile: ${p.profile_key}` : "Profile: —"}
                    {p.created_at ? ` • Created: ${String(p.created_at).slice(0, 10)}` : ""}
                    {p.status ? ` • Status: ${p.status}` : ""}
                  </div>
                  {p.error ? (<div className="body-small" style={{ color: "#b91c1c", marginTop: 4 }}>{p.error}</div>) : null}
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  <button
                    type="button"
                    className="btn-primary"
                    onClick={() => loadProgram(p.program_id)}
                    disabled={programLoading}
                    style={{ padding: "8px 12px", whiteSpace: "nowrap" }}
                    data-testid={`program-open-${p.program_id}`}
                  >
                    {programLoading ? "…" : "Open"}
                  </button>

                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => deleteProgram(p.program_id)}
                    style={{ padding: "8px 12px", whiteSpace: "nowrap", color: "#dc2626", borderColor: "#dc2626" }}
                    disabled={programLoading || programsLoading}
                    data-testid={`program-delete-${p.program_id}`}
                    title="Delete program"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))
          ) : (
            <div className="body-small" style={{ color: "var(--text-muted)" }}>No programs yet.</div>
          )}
        </div>

        {selectedProgram && (
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: "1px solid var(--border-light)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="heading-4" style={{ marginBottom: 4 }}>
                  {selectedProgram.name || "Program"}{" "}
                  <span className="body-small" style={{ color: "var(--text-muted)" }}>
                    ({selectedProgram.program_id})
                  </span>
                </div>
                <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                  {selectedProgram.summary || selectedProgram.program_json?.summary || "Program details"}
                </div>
              </div>

              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  setSelectedProgram(null);
                  setSelectedProgramMeta(null);
                }}
                style={{ padding: "8px 12px" }}
                data-testid="program-close"
              >
                Close
              </button>
            </div>

            {selectedProgramMeta && (
              <>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
                    gap: 10,
                    marginTop: 12,
                  }}
                >
                  {[
                    ["Built from goals", selectedProgramMeta?.counts?.source_goals],
                    ["Added goals", selectedProgramMeta?.counts?.additional_goals],
                    ["Habits", selectedProgramMeta?.counts?.habits],
                    ["Progress tiles", selectedProgramMeta?.counts?.progress_tiles],
                    ["Big graphs", selectedProgramMeta?.counts?.big_graphs],
                    ["Events", selectedProgramMeta?.counts?.events],
                  ].map(([label, value]) => (
                    <div
                      key={label}
                      style={{
                        padding: 12,
                        borderRadius: 12,
                        border: "1px solid var(--border-light)",
                        background: "var(--bg-secondary)",
                      }}
                    >
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>{label}</div>
                      <div style={{ fontWeight: 900, fontSize: 20 }}>{value ?? 0}</div>
                    </div>
                  ))}
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                    gap: 10,
                    marginTop: 14,
                  }}
                >
                  {selectedProgramMeta?.workout_program && (
                    <div style={{ padding: 12, borderRadius: 12, border: "1px solid var(--border-light)", background: "var(--bg-card)" }}>
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>Workout program generated</div>
                      <div style={{ fontWeight: 900 }}>{selectedProgramMeta.workout_program.name || "Workout Plan"}</div>
                      <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
                        {selectedProgramMeta.workout_program.events_count || 0} workouts
                        {selectedProgramMeta.workout_program.start_date ? ` • ${selectedProgramMeta.workout_program.start_date}` : ""}
                        {selectedProgramMeta.workout_program.end_date ? ` to ${selectedProgramMeta.workout_program.end_date}` : ""}
                      </div>
                    </div>
                  )}

                  {selectedProgramMeta?.meal_plan && (
                    <div style={{ padding: 12, borderRadius: 12, border: "1px solid var(--border-light)", background: "var(--bg-card)" }}>
                      <div className="body-small" style={{ color: "var(--text-muted)" }}>Meal plan generated</div>
                      <div style={{ fontWeight: 900 }}>{selectedProgramMeta.meal_plan.name || "Meal Plan"}</div>
                      <div className="body-small" style={{ color: "var(--text-secondary)", marginTop: 4 }}>
                        {selectedProgramMeta.meal_plan.days_count ? `${selectedProgramMeta.meal_plan.days_count} days` : "Generated"}
                        {selectedProgramMeta.meal_plan.status ? ` • ${selectedProgramMeta.meal_plan.status}` : ""}
                      </div>
                    </div>
                  )}
                </div>

                {asList(selectedProgramMeta.source_goals).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 8 }}>Goals used to generate this program</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {asList(selectedProgramMeta.source_goals).map((goal) => (
                        <div key={goal.goal_id} style={{ padding: 10, borderRadius: 10, border: "1px solid var(--border-light)", background: "var(--bg-secondary)" }}>
                          <div style={{ fontWeight: 800 }}>{goal.title || "Goal"}</div>
                          <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                            {goal.description ? `${goal.description} • ` : ""}
                            {goal.due_date ? `Due: ${goal.due_date}` : "No due date"}
                            {goal.target_value != null ? ` • Target: ${goal.target_value}${goal.target_unit ? ` ${goal.target_unit}` : ""}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {asList(selectedProgramMeta.additional_goals).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 8 }}>Additional goals generated by this program</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {asList(selectedProgramMeta.additional_goals).map((goal) => (
                        <div key={goal.goal_id} style={{ padding: 10, borderRadius: 10, border: "1px solid var(--border-light)", background: "var(--bg-secondary)" }}>
                          <div style={{ fontWeight: 800 }}>{goal.title || "Goal"}</div>
                          <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                            {goal.description ? `${goal.description} • ` : ""}
                            {goal.due_date ? `Due: ${goal.due_date}` : "No due date"}
                            {goal.target_value != null ? ` • Target: ${goal.target_value}${goal.target_unit ? ` ${goal.target_unit}` : ""}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {asList(selectedProgramMeta.generated_habits).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 8 }}>Habits generated</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {asList(selectedProgramMeta.generated_habits).map((habit) => (
                        <div key={habit.habit_id} style={{ padding: 10, borderRadius: 10, border: "1px solid var(--border-light)", background: "var(--bg-secondary)" }}>
                          <div style={{ fontWeight: 800 }}>{habit.name || "Habit"}</div>
                          <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                            {(habit.frequency || "daily").toUpperCase()}
                            {habit.interval && Number(habit.interval) > 1 ? ` every ${habit.interval}` : ""}
                            {habit.time_of_day ? ` • ${habit.time_of_day}` : ""}
                            {habit.metric_rule?.domain ? ` • ${habit.metric_rule.domain}.${habit.metric_rule.field} ${habit.metric_rule.operator} ${habit.metric_rule.value}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {asList(selectedProgramMeta.progress_tiles).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 8 }}>Progress tiles generated</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {asList(selectedProgramMeta.progress_tiles).map((tile) => (
                        <div key={tile.id || tile.metric_key} style={{ padding: 10, borderRadius: 10, border: "1px solid var(--border-light)", background: "var(--bg-secondary)" }}>
                          <div style={{ fontWeight: 800 }}>{tile.label || tile.metric_key || "Tile"}</div>
                          <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                            {tile.metric_key}
                            {tile.display ? ` • ${tile.display}` : ""}
                            {tile.timeframe_days ? ` • ${tile.timeframe_days} days` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {asList(selectedProgramMeta.big_graphs).length > 0 && (
                  <div style={{ marginTop: 14 }}>
                    <div className="body-small" style={{ color: "var(--text-muted)", marginBottom: 8 }}>Big graphs generated</div>
                    <div style={{ display: "grid", gap: 8 }}>
                      {asList(selectedProgramMeta.big_graphs).map((tile) => (
                        <div key={tile.id || tile.metric_key} style={{ padding: 10, borderRadius: 10, border: "1px solid var(--border-light)", background: "var(--bg-secondary)" }}>
                          <div style={{ fontWeight: 800 }}>{tile.label || tile.metric_key || "Graph"}</div>
                          <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                            {tile.metric_key}
                            {tile.display ? ` • ${tile.display}` : ""}
                            {tile.timeframe_days ? ` • ${tile.timeframe_days} days` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Create Program Modal (uses GoalModal for new goals) */}
      {showCreateProgram && (
        <div
          role="dialog"
          aria-modal="true"
          onMouseDown={() => !createProgramLoading && setShowCreateProgram(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.60)",
            zIndex: 1000000,
            padding: 16,
            overflowY: "auto",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
          }}
        >
          <div
            className="card"
            onMouseDown={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 720,
              marginTop: 24,
              marginBottom: 24,
              padding: 16,
              background: "var(--bg-modal)",
              border: "1px solid var(--border-light)",
              borderRadius: 16,
              boxShadow: "0 30px 80px rgba(0,0,0,0.35)",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div>
                <div className="heading-4">Create Program</div>
                <div className="body-small" style={{ color: "var(--text-secondary)" }}>
                  Pick a focus profile and attach goals. Create new goals with the Goal Builder.
                </div>
              </div>

              <button type="button" className="btn-secondary" disabled={createProgramLoading} onClick={() => setShowCreateProgram(false)}>
                ✕
              </button>
            </div>

            <div style={{ marginTop: 14 }}>
              <label className="body-small" style={{ color: "var(--text-secondary)" }}>Profile Type</label>
              <select className="form-input" value={profileKey} onChange={(e) => setProfileKey(e.target.value)} style={{ marginTop: 6 }}>
                {PROGRAM_PROFILES.map((p) => (
                  <option key={p.value} value={p.value}>{p.label}</option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: 16, display: "flex", gap: 10, flexWrap: "wrap" }}>
              <button type="button" className="btn-primary" onClick={() => { setShowCreateProgram(false); setTimeout(() => onOpenGoalModal?.("coach_programs"), 0); }} title="Create a new goal first, then refresh this list">
                + New Goal (Goal Builder)
              </button>

              <button type="button" className="btn-secondary" onClick={loadGoals} disabled={goalsLoading}>
                {goalsLoading ? "…" : "Refresh goals"}
              </button>
            </div>

            <div style={{ marginTop: 14 }}>
              <div className="heading-4" style={{ marginBottom: 6 }}>Select Goals for this Program</div>

              {goalsLoading ? (
                <div className="body-small" style={{ color: "var(--text-muted)" }}>Loading goals…</div>
              ) : goals.length === 0 ? (
                <div className="body-small" style={{ color: "var(--text-muted)" }}>
                  No active goals found. Click <strong>New Goal</strong> to create one.
                </div>
              ) : (
                <div style={{ display: "grid", gap: 8 }}>
                  {goals.map((g) => (
                    <label
                      key={g.goal_id}
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "flex-start",
                        padding: 10,
                        borderRadius: 10,
                        border: "1px solid var(--border-light)",
                        background: "var(--bg-secondary)",
                        cursor: "pointer",
                      }}
                    >
                      <input type="checkbox" checked={!!selectedGoalIds[g.goal_id]} onChange={() => toggleGoal(g.goal_id)} style={{ marginTop: 3 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: 800 }}>{g.title || "Goal"}</div>
                        <div className="body-small" style={{ color: "var(--text-muted)" }}>
                          {g.description ? `${g.description} • ` : ""}
                          {g.due_date ? `Due: ${g.due_date}` : "No due date"}
                          {g.target_value != null ? ` • Target: ${g.target_value} ${g.target_unit || ""}` : ""}
                        </div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 16 }}>
              <button type="button" className="btn-secondary" disabled={createProgramLoading} onClick={() => setShowCreateProgram(false)}>
                Cancel
              </button>
              <button type="button" className="btn-primary" disabled={createProgramLoading} onClick={createProgram}>
                {createProgramLoading ? "Creating…" : "Create Program"}
              </button>
            </div>
          </div>
        </div>
      )}

        </>
      )}

      {mode === "chat" && (
        <>
      {/* Chat */}
      <div className="card coach-chat-card">
        <div className="coach-chat-messages">
          {messages.map((msg, index) => (
            <div key={index} style={{ display: "flex", justifyContent: msg.role === "user" ? "flex-end" : "flex-start" }}>
              <div className={`coach-bubble ${msg.role === "user" ? "user" : msg.error ? "error" : "assistant"}`}>
                {msg.content}
              </div>
            </div>
          ))}

          {loading && (
            <div style={{ display: "flex", justifyContent: "flex-start" }}>
              <div style={{ padding: "12px 16px", borderRadius: "16px 16px 16px 4px", background: "var(--bg-secondary)", color: "var(--text-secondary)" }}>
                <span className="loading-dots">Thinking</span>
                <style>{`
                  .loading-dots::after { content: ''; animation: dots 1.5s steps(4, end) infinite; }
                  @keyframes dots {
                    0%, 20% { content: ''; }
                    40% { content: '.'; }
                    60% { content: '..'; }
                    80%, 100% { content: '...'; }
                  }
                `}</style>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        {messages.length === 1 && (
          <div style={{ padding: "12px 20px", borderTop: "1px solid var(--border-light)", display: "flex", gap: 8, flexWrap: "wrap" }}>
            {quickQuestions.map((q, i) => (
              <button
                key={i}
                onClick={() => setInput(q)}
                style={{
                  padding: "6px 12px",
                  borderRadius: "100px",
                  border: "1px solid var(--border-light)",
                  background: "transparent",
                  color: "var(--text-secondary)",
                  fontSize: 12,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
              >
                {q}
              </button>
            ))}
          </div>
        )}

        <form
          onSubmit={sendMessage}
          className="coach-chat-form"
          style={{ padding: "16px 20px", borderTop: "1px solid var(--border-light)" }}
        >
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask your fitness coach..."
            disabled={loading}
            className="form-input coach-chat-input"
            rows={3}
            style={{ margin: 0, width: "100%" }}
            data-testid="coach-input"
          />

          <button
            type="submit"
            id="coach-send-btn"
            name="coach-send-btn"
            disabled={loading || !input.trim()}
            className="btn-primary"
            style={{ padding: "12px 24px", opacity: loading || !input.trim() ? 0.5 : 1 }}
            data-testid="coach-send-btn"
          >
            {loading ? "..." : "Send"}
          </button>
        </form>
      </div>

      <div className="card coach-tip-card" style={{ padding: "16px 20px", marginTop: 16 }}>
        <p className="body-small" style={{ color: "var(--text-secondary)", margin: 0 }}>
          💡 <strong>Tip:</strong> Your chat history is saved automatically. The more you log, the better advice you'll receive!
        </p>
      </div>
        </>
      )}

    </div>
  );
}

export default CoachView;
